import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errOf = (e) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Something went wrong — please try again.");

export default function Register() {
    const nav = useNavigate();
    const { completeSession } = useAuth();
    const [step, setStep] = useState(1);
    const [f, setF] = useState({ name: "", email: "", password: "" });
    const [code, setCode] = useState("");
    const [hint, setHint] = useState("");
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

    const register = async () => {
        if (!f.name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email) || f.password.length < 8) return toast.error("Enter your name, a valid email and a password of 8+ characters.");
        setBusy(true);
        try { const { data } = await axios.post(`${API}/auth/prospect/register`, f); setHint(data.email_hint); setStep(2); }
        catch (e) { toast.error(errOf(e)); } finally { setBusy(false); }
    };
    const verify = async () => {
        setBusy(true);
        try { const { data } = await axios.post(`${API}/auth/prospect/verify`, { email: f.email, code }, { withCredentials: true }); completeSession(data); nav("/my-quotes", { replace: true }); }
        catch (e) { toast.error(errOf(e)); } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-[#f4f7fc] grid place-items-center px-6">
            <div className="w-full max-w-md bg-white border border-border rounded-md p-8" data-testid="register-card">
                <div className="text-xs font-semibold text-brand uppercase tracking-wider">Qube Hub</div>
                <h1 className="font-display font-bold text-2xl mt-1 mb-1">Create your account</h1>
                <p className="text-sm text-muted-foreground mb-5">So you can return to a quote, accept it and track progress.</p>
                {step === 1 ? (
                    <div className="space-y-3">
                        <label className="block text-sm"><span className="font-medium text-[13px] block mb-1">Your name</span><input data-testid="reg-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="qi" /></label>
                        <label className="block text-sm"><span className="font-medium text-[13px] block mb-1">Email</span><input data-testid="reg-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="qi" /></label>
                        <label className="block text-sm"><span className="font-medium text-[13px] block mb-1">Password (8+ characters)</span><input data-testid="reg-password" type="password" value={f.password} onChange={(e) => set("password", e.target.value)} className="qi" /></label>
                        <button data-testid="reg-submit" disabled={busy} onClick={register} className="w-full bg-brand hover:bg-brand-hover text-white text-sm font-semibold py-3 rounded-sm disabled:opacity-40 flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Send verification code</button>
                        <p className="text-xs text-center text-muted-foreground">Already have a login? <Link to="/login" className="text-brand underline">Sign in</Link></p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">We emailed a 6-digit code to <strong className="text-foreground">{hint}</strong>.</p>
                        <input data-testid="reg-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="______" maxLength={6} className="qi text-center text-xl tracking-[0.5em] font-num" />
                        <button data-testid="reg-verify" disabled={busy || code.length < 6} onClick={verify} className="w-full bg-brand hover:bg-brand-hover text-white text-sm font-semibold py-3 rounded-sm disabled:opacity-40 flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Verify & continue</button>
                        <button onClick={() => setStep(1)} className="w-full text-xs text-muted-foreground hover:underline">Change details</button>
                    </div>
                )}
                <style>{`.qi{width:100%;padding:10px 12px;border:1px solid #D5DEEC;border-radius:4px;background:#fff;font-size:14px}.qi:focus{outline:none;border-color:#004AAD;box-shadow:0 0 0 3px rgba(0,74,173,.16)}`}</style>
            </div>
        </div>
    );
}
